import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import AddButton from "../../buttons/button";
import InputField from "../../forms/input-field";
import SelectField from "../../forms/select-field";
import DatePickerInput from "../../forms/date-picker";
import TabsComponent from "../../helper-components/tabs-component";
import MapsAndPhotos from "./maps-photos";
import StoreShopAddressTab from "./shop-address";
import StoreDetailsTab from "./store-details";
import StoreRemarksTab from "./remarks";
import StoreMinMaxTab from "./min-max";
import { useDispatch } from "react-redux";
import {
  StoreAddressCreateAction,
  StoreAddressUpdateAction,
} from "../../../redux/actions/store-action";
import { api } from "../../../redux/apis/api";
import successToast from "../../helper-components/success-toast";
import errorToast from "../../helper-components/error-toast";
import { isAdmin } from "../../../redux/config";
import ApproveDeclineActions from "../approve-decline-actions";
import { autoGenerateStoreCodeApi } from "../../../redux/apis/store-api";
import { storeEmployeeDropdownApi } from "../../../redux/apis/store-employee-api";

// The store-code endpoint requires a server_name and nothing maps a store to a
// server yet, so it is sent as a constant. One line to change once that exists.
const STORE_CODE_SERVER_NAME = "SKIA";
// Stores onboarded through this flow are franchise-owned unless the record
// says otherwise.
const DEFAULT_STORE_CATEGORY = "FOFO";

// First argument that actually carries a value.
const firstFilled = (...values) =>
  values.find((v) => v !== null && v !== undefined && v !== "");

// Strips formatting — a number input renders blank for values it cannot parse.
const toNumericValue = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "";
  if (typeof raw === "number") return raw;
  const cleaned = String(raw).replace(/[^0-9.]/g, "");
  return cleaned === "" ? "" : Number(cleaned);
};

// Backend seeds min/max deviation fields at 0 before a real value is ever
// entered — show those as a blank field so the user can type straight in
// instead of deleting a pre-filled "0.00" first.
const blankIfZero = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "";
  const num = parseFloat(raw);
  return !isNaN(num) && num === 0 ? "" : raw;
};

// Days defaults to 60 until the user edits it, instead of starting blank/0.
const withDefault = (raw, defaultValue) => {
  if (raw === null || raw === undefined || raw === "") return defaultValue;
  const num = parseFloat(raw);
  return !isNaN(num) && num === 0 ? defaultValue : raw;
};

const StoreAddressTab = ({
  setId,
  setStoreData,
  storeData,
  initialValue,
  id,
  permissionFn = () => {},
  disabled = false,
  // True while the LOI is declined: an approved location becomes editable.
  reopenedForRework = false,
  // Reports a finished step to the tab container, which unlocks the next tab.
  onProgress = () => {},
}) => {
  const [currentTab, setCurrentTab] = useState("Shop Address");
  const [storeTypeOptions, setStoreTypeOptions] = useState([]);
  const [relationshipManagerOptions, setRelationshipManagerOptions] = useState([]);
  const [storeTypeMap, setStoreTypeMap] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    api
      .get("/fofo/storeType/getAll")
      .then((res) => {
        const list = res.data?.data ?? [];
        console.log("Store types fetched:", list);
        const options = list.map((t) => ({ label: t.store_type, value: t.store_type }));
        const map = {};
        list.forEach((t) => { map[t.store_type] = t; });
        setStoreTypeOptions(options);
        setStoreTypeMap(map);
      })
      .catch((err) => {
        console.error("Failed to fetch store types:", err?.response?.data ?? err?.message);
      });
  }, []);
  // Relationship Managers come from the Employee Master. Existing records store
  // the employee's sem_id, so that is the option value; SelectField resolves it
  // back to the name for display. A store saved earlier with a free-text name
  // still shows that text, since SelectField falls back to the raw value when it
  // matches no option.
  useEffect(() => {
    // GET /store-employees/dropdown?pageNumber=1&pageSize=20
    storeEmployeeDropdownApi({ pageNumber: 1, pageSize: 20 })
      .then((res) => {
        // The backend answers 200 even on failure, with the outcome in the body.
        if (res?.data?.status === false) {
          console.error(
            "Failed to fetch relationship managers:",
            res.data?.message
          );
          setRelationshipManagerOptions([]);
          return;
        }
        const rows = res.data?.data?.rows ?? [];
        setRelationshipManagerOptions(
          rows
            .filter((r) => r?.sem_id && r?.employee_name)
            .map((r) => ({
              label: r.employee_code
                ? `${r.employee_name} (${r.employee_code})`
                : r.employee_name,
              value: r.sem_id,
            }))
        );
      })
      .catch((err) => {
        console.error(
          "Failed to fetch relationship managers:",
          err?.response?.data ?? err?.message
        );
        setRelationshipManagerOptions([]);
      });
  }, []);

  // `is_dl_available` has no backend column of its own, so it is inferred from the
  // Drug License validity date: a date means Yes, and a saved store with no date
  // means No — so a "No" answered in the app shows as No instead of an empty
  // field. Only a store with nothing loaded yet stays blank.
  const dlValidDate =
    initialValue?.dl_valid_date ??
    initialValue?.dl_valid_till_date ??
    initialValue?.dl_valid_upto ??
    "";
  const hasStoreRecord = Object.keys(initialValue ?? {}).length > 0;
  const inferredDlAvailable = dlValidDate ? true : hasStoreRecord ? false : null;

  const storedType = storeTypeMap[initialValue?.store_type] ?? null;
  const initialValues = {
    ...(id && { store_id: id }), // Include store_id only if id is present
    store_name: initialValue?.store_name ?? "",
    store_code: initialValue?.store_code ?? "",
    store_type: initialValue?.store_type ?? "",
    base_commission: initialValue?.base_commission ?? storedType?.base_commision ?? "",
    additional_margin: initialValue?.additional_margin ?? storedType?.additional_margin ?? "",
    approx_capex: initialValue?.approx_capex ?? storedType?.capex ?? "",
    franchise_fee: initialValue?.franchise_fee ?? storedType?.franchidse_fee ?? "",
    lock_in_period: initialValue?.lock_in_period ?? storedType?.lock_in_period ?? "",
    carpet_area: initialValue?.carpet_area ?? initialValue?.corpet_area ?? "",
    shop_length: initialValue?.shop_length ?? "",
    shop_width: initialValue?.shop_width ?? "",
    shop_height: initialValue?.shop_height ?? "",
    shutter_width: initialValue?.shutter_width ?? "",
    shutter_height: initialValue?.shutter_height ?? "",
    washroom_availability: initialValue?.washroom_availability ?? null,
    is_dl_available: initialValue?.is_dl_available ?? inferredDlAvailable,
    dl_valid_date: dlValidDate,
    store_360_video: initialValue?.store_360_video ?? initialValue?.video_url ?? "",
    relationship_manager: initialValue?.relationship_manager ?? "",
    reapproval_remarks: initialValue?.reapproval_remarks ?? "",
    addres_remarks: initialValue?.addres_remarks ?? "",
    is_approve_location: initialValue?.is_approve_location ?? false,
    is_reject_location: initialValue?.is_reject_location ?? false,
    shop_address: initialValue?.shop_address ?? "",
    shop_area: initialValue?.shop_area ?? "",
    shop_landmark: initialValue?.shop_landmark ?? "",
    shop_town_or_village: initialValue?.shop_town_or_village ?? "",
    shop_city: initialValue?.shop_city ?? "",
    shop_state: initialValue?.shop_state ?? "",
    shop_pin_code: initialValue?.shop_pin_code ?? "",
    shop_country_region: initialValue?.shop_country_region ?? "",
    shop_region: initialValue?.shop_region ?? "", // new

    property_owned_type: initialValue?.property_owned_type ?? null,
    // Rent lives in `store_rent`; `store_rent_per_month` is a legacy null column.
    store_rent_per_month: toNumericValue(
      firstFilled(initialValue?.store_rent_per_month, initialValue?.store_rent)
    ),
    store_duration_concurrently:
      initialValue?.store_duration_concurrently ?? "",
    store_sale_per_day: initialValue?.store_sale_per_day ?? null,
    store_medicine_sale: initialValue?.store_medicine_sale ?? null,
    store_otc_sale: initialValue?.store_otc_sale ?? null,
    store_latitude: initialValue?.store_latitude ?? "",
    store_longitude: initialValue?.store_longitude ?? "",
    // Older records carry the three images as separate columns rather than the
    // array, so fall back to them instead of showing an empty gallery.
    shop_images: Array.isArray(initialValue?.shop_images)
      ? initialValue.shop_images
      : [
          initialValue?.shop_image_1,
          initialValue?.shop_image_2,
          initialValue?.shop_image_3,
        ].filter(Boolean),
    tab_remarks: initialValue?.tab_remarks ?? "",
    avg_days: withDefault(initialValue?.avg_days, 60),
    min_percentage: blankIfZero(initialValue?.min_percentage),
    max_percentage: blankIfZero(initialValue?.max_percentage),
  };
  const validationSchema = Yup.object().shape({
    store_type: Yup.string().required("Store Type is required"),
    relationship_manager: Yup.string().required(
      "Relationship Manager is required"
    ),
    shop_address: Yup.string().required("Shop Address is required"),
    shop_area: Yup.string().required("Shop Area is required"),
    shop_landmark: Yup.string().required("Shop Landmark is required"),
    shop_town_or_village: Yup.string().required("Town or Village is required"),
    shop_city: Yup.string().required("City is required"),
    shop_state: Yup.string().required("State is required"),
    shop_pin_code: Yup.string()
      .required("Pin Code is required")
      .matches(/^[1-9][0-9]{5}$/, "Invalid Pin Code"),
    shop_country_region: Yup.string().required("Country is required"),
    shop_region: Yup.string().required("Region is required"),

    store_rent_per_month: Yup.number().required("Rent Per Month is required"),
    store_duration_concurrently: Yup.string().required(
      "Store Duration Concurrently is required"
    ),
    store_sale_per_day: Yup.number().required("Store Sale Per Day is required"),
    store_medicine_sale: Yup.number().required("Medicine Sale is required"),
    store_otc_sale: Yup.number().required("OTC Sale is required"),
    store_latitude: Yup.string().required("Store Latitude is required"),
    store_longitude: Yup.string().required("Store Longitude is required"),
    shop_images: Yup.array()
      .min(2, "At least 2 images are required")
      .required("At least 2 images are required"),
  });

  // Only these three are enforced today. The fuller validationSchema above stays
  // defined but unapplied: switching it on would make 19 fields mandatory at once
  // and block saves on existing stores that predate those rules.
  const requiredFieldsSchema = Yup.object().shape({
    store_type: Yup.string().required("Store Type is required"),
    store_latitude: Yup.string().required("Store Latitude is required"),
    store_longitude: Yup.string().required("Store Longitude is required"),
  });

  // Shapes form values into the Store Address payload. Extracted so the approval
  // flow can re-save the store with its new code using exactly the same rules as
  // a normal Save — the two must not drift apart.
  //
  // dl_valid_date is a read-only mirror of the Drug License record's
  // dl_valid_upto field (edited on the Drug License tab). Store Details must
  // never write Drug License fields, so it is stripped here.
  // is_dl_available has no backend field anywhere — intentionally never sent.
  const buildAddressPayload = (values) => {
    // Approval decisions belong to /store/approve and /store/reject alone. This
    // payload is built by spreading the form, so they are stripped explicitly —
    // otherwise any Save would post whatever the form happened to be holding and
    // could overwrite a decision made elsewhere.
    const decisionFields = [
      "is_approve_location",
      "is_reject_location",
      "is_loi_approved",
      "is_loi_rejected",
      "is_francise_fee_approved",
      "is_francise_fee_rejected",
      "is_approve_dl",
      "is_reject_dl",
      "store_agreement_is_approve",
      "store_agreement_is_reject",
    ];

    const autoPopulatedFields = [
      "base_commission", "additional_margin", "approx_capex",
      "franchise_fee", "lock_in_period",
      "shop_length", "shop_width", "shop_height",
      "shutter_width", "shutter_height",
      "washroom_availability", "is_dl_available", "dl_valid_date",
      "store_360_video",
    ];
    const cleanedValues = { ...values };

    autoPopulatedFields.forEach((field) => delete cleanedValues[field]);
    decisionFields.forEach((field) => delete cleanedValues[field]);

    if ("carpet_area" in cleanedValues) {
      cleanedValues.corpet_area = cleanedValues.carpet_area;
      delete cleanedValues.carpet_area;
    }

    // Left blank on the Min - Max tab means "not entered" — send a number,
    // not an empty string/null. Days defaults to 60; the deviations to 0.
    const emptyFallbacks = { avg_days: 60, min_percentage: 0, max_percentage: 0 };
    Object.keys(emptyFallbacks).forEach((field) => {
      if (
        cleanedValues[field] === "" ||
        cleanedValues[field] === null ||
        cleanedValues[field] === undefined
      ) {
        cleanedValues[field] = emptyFallbacks[field];
      }
    });

    return cleanedValues;
  };

  const onSubmit = (values, actions) => {
    const isUpdate = !!id;
    const franchiseFee = values.franchise_fee;
    const actionType = isUpdate ? StoreAddressUpdateAction : StoreAddressCreateAction;
    dispatch(
      actionType(
        buildAddressPayload(values),
        (data) => onSuccess(data, franchiseFee),
        onError
      )
    );
  };

  const onSuccess = (data, franchiseFee) => {
    const storeId = data?.data?.store_id ?? id;
    setStoreData({
      ...storeData,
      address: { ...data?.data, franchise_fee: franchiseFee },
    });
    successToast(data?.message);
    setId(storeId);
  };
  const onError = (data) => {
    console.log("Address tab error:", data);
    errorToast(data?.message || data?.data?.message || "Something went wrong");
  };

  const [textToCopy, setTextToCopy] = useState(id);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      successToast("Text copied to clipboard!");
    } catch (err) {
      errorToast("Failed to copy text:", err);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={requiredFieldsSchema}
      onSubmit={onSubmit}
    >
      {({ values, setFieldValue, isSubmitting, errors, submitForm, initialValues: savedValues }) => {
        // An approved location is final: the whole tab, Save and Decline
        // included, goes read-only as soon as Approve succeeds — unless the
        // LOI was declined, which reopens it for rework.
        const tabDisabled =
          disabled || (!!values?.is_approve_location && !reopenedForRework);
        return (
          <Form style={{ height: "calc(100% - 5rem)" }} className="pt-5">
            <div className="flex flex-col justify-between h-full ">
              <div>
                <div className="w-full 2xl:w-[40%] xl:w-[50%] lg:w-[60%] px-5 xl:px-10">
                  {id && (
                    <div className="flex items-start gap-2 w-full">
                      <InputField
                        defaultInputClass="inputField focus_outline__none border-0"
                        containerClass="w-full"
                        isDisabled={true}
                        type="text"
                        name="store_id"
                        placeholder="Enter Store Type"
                        label="POS Integration Id"
                        // isRequired={true}
                        labelWidth="10.5rem"
                      />
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="rounded-md px-4 py-3 text-white bg-027A48 text-sm"
                      >
                        Copy
                      </button>
                    </div>
                  )}
                  <InputField
                    isDisabled={tabDisabled}
                    type="text"
                    name="store_name"
                    placeholder="Enter Store Name"
                    label="Store Name"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={tabDisabled}
                    type="text"
                    name="store_code"
                    placeholder="Enter Store Code"
                    label="Store Code"
                    labelWidth="10.5rem"
                  />
                  <SelectField
                    isDisabled={tabDisabled}
                    placeholder="Select Store Type"
                    options={storeTypeOptions}
                    label="Store Type"
                    id="store_type"
                    name="store_type"
                    isRequired={true}
                    isSearchable={true}
                    errMsgContainerClass="ps-2"
                    labelWidth="10.5rem"
                    handleSelected={(selected) => {
                      const t = storeTypeMap[selected?.value];
                      if (t) {
                        setFieldValue("base_commission", t.base_commision ?? "");
                        setFieldValue("additional_margin", t.additional_margin ?? "");
                        setFieldValue("approx_capex", t.capex ?? "");
                        setFieldValue("franchise_fee", t.franchidse_fee ?? "");
                        setFieldValue("lock_in_period", t.lock_in_period ?? "");
                      }
                    }}
                  />
                  <InputField
                    isDisabled={true}
                    type="text"
                    name="base_commission"
                    placeholder="Auto-filled from Store Type"
                    label="Base Commission"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={true}
                    type="text"
                    name="additional_margin"
                    placeholder="Auto-filled from Store Type"
                    label="Additional Margin"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={true}
                    type="text"
                    name="approx_capex"
                    placeholder="Auto-filled from Store Type"
                    label="Approx Capex"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={true}
                    type="text"
                    name="franchise_fee"
                    placeholder="Auto-filled from Store Type"
                    label="Franchise Fee"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={true}
                    type="text"
                    name="lock_in_period"
                    placeholder="Auto-filled from Store Type"
                    label="Lock-in Period"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={tabDisabled}
                    type="number"
                    name="carpet_area"
                    placeholder="Enter Carpet Area"
                    label="Carpet Area"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={tabDisabled}
                    type="number"
                    name="shop_length"
                    placeholder="Enter Shop Length"
                    label="Shop Length"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={tabDisabled}
                    type="number"
                    name="shop_width"
                    placeholder="Enter Shop Width"
                    label="Shop Width"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={tabDisabled}
                    type="number"
                    name="shop_height"
                    placeholder="Enter Shop Height"
                    label="Shop Height"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={tabDisabled}
                    type="number"
                    name="shutter_width"
                    placeholder="Enter Shutter Width"
                    label="Shutter Width"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={tabDisabled}
                    type="number"
                    name="shutter_height"
                    placeholder="Enter Shutter Height"
                    label="Shutter Height"
                    labelWidth="10.5rem"
                  />
                  <SelectField
                    isDisabled={tabDisabled}
                    placeholder="Select Washroom Availability"
                    options={yesNoOptions}
                    label="Washroom Availability"
                    id="washroom_availability"
                    name="washroom_availability"
                    labelWidth="10.5rem"
                  />
                  {/* TODO: is_dl_available has no backend column yet (Store
                      Address or Drug License) — disabled until backend
                      support lands so it isn't mistaken for a persisted
                      field. */}
                  <SelectField
                    isDisabled={true}
                    placeholder="Select DL Availability"
                    options={yesNoOptions}
                    label="Is DL Available"
                    id="is_dl_available"
                    name="is_dl_available"
                    labelWidth="10.5rem"
                  />
                  {/* Read-only mirror of the Drug License record — edit the
                      validity date on the Drug License tab instead; this
                      display is never sent back on Store Details save. */}
                  <div className="flex items-center mb-2">
                    <span
                      style={{ minWidth: "10.5rem" }}
                      className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]"
                    >
                      DL Valid Date
                    </span>
                    <DatePickerInput
                      isDisabled={true}
                      placeholder="Select DL Valid Date"
                      value={values.dl_valid_date}
                      onChange={(date) =>
                        setFieldValue("dl_valid_date", date)
                      }
                    />
                  </div>
                  <SelectField
                    isDisabled={tabDisabled}
                    name="relationship_manager"
                    id="relationship_manager"
                    placeholder="Select Relationship Manager"
                    label="Relationship Manager"
                    options={relationshipManagerOptions}
                    isSearchable={true}
                    isClearable={true}
                    labelWidth="10.5rem"
                    errMsgContainerClass="ps-2"
                  />
                  <InputField
                    isDisabled={tabDisabled}
                    type="text"
                    name="reapproval_remarks"
                    placeholder="Enter Re-approval Remarks"
                    label="Re-approval Remarks"
                    labelWidth="10.5rem"
                  />
                  <InputField
                    isDisabled={tabDisabled}
                    type="text"
                    name="addres_remarks"
                    placeholder="Enter Remarks"
                    label="Remarks"
                    labelWidth="10.5rem"
                  />{" "}
                  {permissionFn("store_document_process")?.approver && (
                    <ApproveDeclineActions
                      storeId={id}
                      approveKey="is_approve_location"
                      rejectKey="is_reject_location"
                      record={initialValue}
                      isApproved={!!values?.is_approve_location}
                      isRejected={!!values?.is_reject_location}
                      disabled={tabDisabled}
                      formValues={values}
                      savedValues={savedValues}
                      // beforeApprove issues the store code and saves it, so it
                      // is part of the decision rather than an unsaved edit.
                      extraDecisionKeys={["store_code"]}
                      // A store approved without a code gets one issued and saved
                      // first; one that already has a code goes straight to the
                      // approval call.
                      beforeApprove={async () => {
                        if (values?.store_code) return true;

                        const res = await autoGenerateStoreCodeApi({
                          server_name: STORE_CODE_SERVER_NAME,
                          store_type: values?.store_type,
                          store_category:
                            initialValue?.store_category || DEFAULT_STORE_CATEGORY,
                          state: values?.shop_state,
                          city: values?.shop_city,
                        });
                        if (res?.data?.status === false) {
                          errorToast(
                            res.data?.message ?? "Could not generate the store code"
                          );
                          return false;
                        }
                        const code =
                          res?.data?.data?.store_code ?? res?.data?.data;
                        if (!code || typeof code !== "string") {
                          errorToast("Store code was not returned");
                          return false;
                        }

                        await setFieldValue("store_code", code);
                        // Persist the code before the decision is recorded, so an
                        // approved store always has one on the server too.
                        await new Promise((resolve, reject) => {
                          dispatch(
                            StoreAddressUpdateAction(
                              { ...buildAddressPayload(values), store_code: code },
                              resolve,
                              reject
                            )
                          );
                        });
                        return true;
                      }}
                      onDecided={async (data) => {
                        onProgress({
                          is_approve_location: !!data?.is_approve_location,
                        });
                        await setFieldValue(
                          "is_approve_location",
                          !!data?.is_approve_location
                        );
                        await setFieldValue(
                          "is_reject_location",
                          !!data?.is_reject_location
                        );
                      }}
                    />
                  )}
                </div>
                <TabsComponent
                  handleClickfn={(item) => {
                    setCurrentTab(item);
                  }}
                  itemTabs={itemTabs}
                  setCurrentTab={setCurrentTab}
                  currentTab={currentTab}
                />
                {currentTab === "Shop Address" && (
                  <StoreShopAddressTab disabled={tabDisabled} />
                )}
                {currentTab === "Store Rent" && (
                  <StoreDetailsTab disabled={tabDisabled} id={id} />
                )}{" "}
                {currentTab === "Maps & Photos" && (
                  <MapsAndPhotos disabled={tabDisabled} />
                )}
                {currentTab === "Remarks" && (
                  <StoreRemarksTab disabled={tabDisabled} />
                )}
                {currentTab === "Min - Max" && isAdmin && (
                  <StoreMinMaxTab disabled={tabDisabled} />
                )}
              </div>
              {!tabDisabled && (
                <div
                  style={{
                    bottom: "0",
                  }}
                  className="flex justify-end px-10 mb-5x sticky bg-white py-4 invoice-bottom-container"
                >
                  <AddButton
                    onclkFn={() => {
                      if (Object.keys(errors).length !== 0) {
                        const currentIndex = itemTabs.indexOf(currentTab);
                        let nextTab;

                        if (currentIndex !== -1) {
                          // Go to the next tab based on the current tab index
                          nextTab =
                            currentIndex === itemTabs.length - 1
                              ? itemTabs[0] // Loop back to the first tab if the last tab is reached
                              : itemTabs[currentIndex + 1];

                          setCurrentTab(nextTab);
                        }
                      }
                    }}
                    prefixIcon={false}
                    text="Save"
                    type="submit"
                  />
                </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default StoreAddressTab;
const itemTabs = isAdmin
  ? ["Shop Address", "Store Rent", "Maps & Photos", "Remarks", "Min - Max"]
  : ["Shop Address", "Store Rent", "Maps & Photos", "Remarks"];
const yesNoOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];